import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useTranslation } from "react-i18next";
import { img } from "../constants/assets";
import { postApi } from "../services/apiService";

const companyTypeKeys = [
  "telecomOperator",
  "mvno",
  "reseller",
  "technologyCompany",
  "other",
];

const businessTypeKeys = ["wholesale", "whiteLabel", "apiIntegration", "reseller"];

const countryKeys = ["uae", "us", "uk", "india"];

function PartnerModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: null, message: "" });
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setStatus({ type: null, message: "" });
      setIsSubmitting(false);
      setPhone("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: null, message: "" });

    const formData = new FormData(event.target);
    const fullPhone = phone.replace(/\s/g, "");
    const extractedCountryCode =
      "+" + (fullPhone.match(/^\d{1,4}/)?.[0] || "971");
    const extractedPhoneNumber = fullPhone.replace(/^\d{1,4}/, "");

    const payload = {
      fullName: formData.get("fullName")?.trim(),
      email: formData.get("email")?.trim(),
      companyName: formData.get("companyName"),
      countryCode: extractedCountryCode,
      phoneNumber: extractedPhoneNumber,
      businessType: formData.get("businessType"),
      country: formData.get("country"),
      description: formData.get("description")?.trim(),
    };

    setIsSubmitting(true);

    try {
      await postApi("/api/partnership", payload);
      event.target.reset();
      setPhone("");
      setStatus({
        type: "success",
        message: t("partnerModal.success"),
      });
      setTimeout(onClose, 2000);
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error.response?.data?.message ||
          error.message ||
          t("partnerModal.error"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="book-demo-overlay" onClick={onClose} role="presentation">
      <div
        className="book-demo-modal partner-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="partner-modal-title"
      >
        <button
          type="button"
          className="book-demo-close"
          onClick={onClose}
          aria-label={t("common.close")}
        >
          &times;
        </button>

        <div className="book-demo-content-wrap">
          <div className="book-demo-image">
            <img src={img("partnership.svg")} alt="" aria-hidden="true" />
          </div>

          <div className="book-demo-content">
            <h2 id="partner-modal-title" className="book-demo-title">
              {t("partnerModal.title")}
            </h2>
            <p className="book-demo-subtitle">{t("partnerModal.subtitle")}</p>

            <form className="book-demo-form" onSubmit={handleSubmit}>
              <div className="book-demo-form-grid">
                <div className="book-demo-field">
                  <label htmlFor="partner-name">{t("common.fullName")}</label>
                  <input
                    id="partner-name"
                    name="fullName"
                    type="text"
                    placeholder={t("bookDemo.fullNamePlaceholder")}
                    required
                  />
                </div>

                <div className="book-demo-field">
                  <label htmlFor="partner-company">
                    {t("partnerModal.companyName")}
                  </label>
                  <select
                    id="partner-company"
                    name="companyName"
                    defaultValue=""
                    required
                  >
                    <option value="" disabled>
                      {t("common.select")}
                    </option>
                    {companyTypeKeys.map((key) => (
                      <option
                        key={key}
                        value={t(`partnerModal.companyTypes.${key}`)}
                      >
                        {t(`partnerModal.companyTypes.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="book-demo-field">
                  <label htmlFor="partner-email">{t("common.email")}</label>
                  <input
                    id="partner-email"
                    name="email"
                    type="email"
                    placeholder={t("bookDemo.emailPlaceholder")}
                    required
                  />
                </div>

                <div className="book-demo-field">
                  <label htmlFor="partner-phone">{t("common.phoneNumber")}</label>
                  <PhoneInput
                    country="ae"
                    value={phone}
                    onChange={setPhone}
                    inputProps={{
                      id: "partner-phone",
                      name: "phoneNumber",
                      required: true,
                    }}
                    inputClass="book-demo-phone-input"
                    containerClass="phone-input-container"
                    buttonClass="country-dropdown-button"
                    dropdownClass="country-dropdown"
                    searchClass="country-search"
                    enableSearch
                    disableSearchIcon
                    searchPlaceholder={t("common.searchCountry")}
                    placeholder={t("common.enterPhone")}
                  />
                </div>

                <div className="book-demo-field">
                  <label htmlFor="partner-business-type">
                    {t("partnerModal.businessType")}
                  </label>
                  <select
                    id="partner-business-type"
                    name="businessType"
                    defaultValue=""
                    required
                  >
                    <option value="" disabled>
                      {t("common.select")}
                    </option>
                    {businessTypeKeys.map((key) => (
                      <option
                        key={key}
                        value={t(`partnerModal.businessTypes.${key}`)}
                      >
                        {t(`partnerModal.businessTypes.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="book-demo-field">
                  <label htmlFor="partner-country">{t("partnerModal.country")}</label>
                  <select
                    id="partner-country"
                    name="country"
                    defaultValue=""
                    required
                  >
                    <option value="" disabled>
                      {t("common.select")}
                    </option>
                    {countryKeys.map((key) => (
                      <option key={key} value={t(`contact.countries.${key}`)}>
                        {t(`contact.countries.${key}`)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="book-demo-field book-demo-field--message">
                <label htmlFor="partner-message">
                  {t("partnerModal.tellUsMore")}
                </label>
                <textarea
                  id="partner-message"
                  name="description"
                  placeholder={t("partnerModal.businessPlaceholder")}
                  rows={4}
                  required
                />
              </div>

              {status.message && (
                <p
                  className={`contact-form-status contact-form-status--${status.type}`}
                  role="alert"
                >
                  {status.message}
                </p>
              )}

              <div className="book-demo-actions d-flex justify-content-between align-items-center w-100">
                <label className="book-demo-subtitle mb-0">
                  <input type="checkbox" required /> {t("partnerModal.agreeTerms")}{" "}
                  <Link to="/terms" target="_blank" rel="noopener noreferrer">
                    {t("common.termsAndConditions")}
                  </Link>{" "}
                  {t("partnerModal.and")}{" "}
                  <Link to="/privacy" target="_blank" rel="noopener noreferrer">
                    {t("common.privacyPolicy")}
                  </Link>
                </label>
                <button
                  type="submit"
                  className="book-demo-submit"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t("common.submitting") : t("common.submit")}
                  <img src={img("arrow.svg")} alt="" aria-hidden="true" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PartnerModal;
